import React, { useCallback, useEffect, useState } from "react";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import {
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminSettings } from "../store/admin";
import { formatDate } from "./components/invoices/invoiceUtils";

const FIELDS = [
  {
    key: "bestsellerEnrollmentThreshold",
    label: "Bestseller enrollment threshold",
    description: "Enrollments required for a course to be marked a bestseller.",
  },
  {
    key: "hotEnrollmentThreshold",
    label: "Hot enrollment threshold",
    description: "Enrollments required within the window for a course to be marked hot.",
  },
  {
    key: "hotEnrollmentWindowDays",
    label: "Hot enrollment window (days)",
    description: "Rolling window used to count hot enrollments.",
  },
];

const Settings = () => {
  const {
    settings,
    settingsLoading,
    settingsError,
    settingsMutationLoading,
    settingsMutationError,
    fetchSettings,
    updateSettings,
  } = useAdminSettings();

  const [form, setForm] = useState({
    bestsellerEnrollmentThreshold: "",
    hotEnrollmentThreshold: "",
    hotEnrollmentWindowDays: "",
  });
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (!settings) return;
    setForm({
      bestsellerEnrollmentThreshold: settings.bestsellerEnrollmentThreshold ?? "",
      hotEnrollmentThreshold: settings.hotEnrollmentThreshold ?? "",
      hotEnrollmentWindowDays: settings.hotEnrollmentWindowDays ?? "",
    });
  }, [settings]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {};
    FIELDS.forEach((field) => {
      const value = form[field.key];
      if (value !== "" && value !== null && value !== undefined) {
        const numeric = Number(value);
        if (!Number.isInteger(numeric) || numeric < 0) return;
        payload[field.key] = numeric;
      }
    });
    if (Object.keys(payload).length === 0) {
      showToast("Enter at least one threshold value", false);
      return;
    }
    const result = await updateSettings(payload);
    if (!result.success) {
      showToast(result.error || "Failed to update settings", false);
      return;
    }
    showToast("Settings updated", true);
  };

  return (
    <div>
      <AdminNavBar title="Platform Settings" />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <AdminSectionHeader
          title="Platform Settings"
          description="Badge thresholds used across the marketplace."
          count={settings?.updatedAt ? `Updated ${formatDate(settings.updatedAt)}` : "Defaults"}
        />

        {settingsLoading ? (
          <AdminLoadingState />
        ) : (
          <AdminTablePanel>
            <form onSubmit={handleSubmit} className="grid gap-4 p-4 md:grid-cols-3">
              {FIELDS.map((field) => (
                <label
                  key={field.key}
                  className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500"
                >
                  {field.label}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form[field.key]}
                    onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal"
                  />
                  <span className="text-[11px] font-normal normal-case tracking-normal text-slate-500">
                    {field.description}
                  </span>
                </label>
              ))}

              <div className="md:col-span-3">
                <button type="submit" className={adminPrimaryButton} disabled={settingsMutationLoading}>
                  {settingsMutationLoading ? "Saving…" : "Save Settings"}
                </button>
              </div>
            </form>
          </AdminTablePanel>
        )}

        {(settingsError || settingsMutationError) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {settingsError || settingsMutationError}
          </div>
        )}
      </AdminPageBody>
    </div>
  );
};

export default Settings;
